import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import { Vehicle } from 'components/Erp/Display';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getBaseDrivers, removeDriverFromBase } from 'service/DriverRegistrationService';
import { getErpErrorMessage } from 'service/ErpService';
import { hasAccess } from 'utils/access';
import DriverBaseDialog from '../../AdminPanelPage/DriverBaseDialog/DriverBaseDialog.tsx';
import Notification from 'utils/Notifications';

const useIsMobile = (breakpoint = 600) => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
};

const STATUS_COLOR = {
  'Въехал': { bg: "var(--theme-primary-soft, #eeedff)", color: "var(--theme-primary-text, #0d00f3)" },
  'В очереди': { bg: "var(--theme-primary-soft, #eeedff)", color: "var(--theme-primary-text, #0d00f3)" },
  'На ремонте': { bg: "var(--theme-warning-soft, #fbf4e7)", color: "var(--theme-warning-text, #9b7129)" },
  'В погрузке': { bg: "var(--theme-orange-soft, #fff7ed)", color: "var(--theme-danger-text, #c2410c)" },
  'В пути': { bg: "var(--theme-info-soft, #eff6ff)", color: "var(--theme-info-text, #1d4ed8)" },
  'Свободен': { bg: "var(--theme-success-soft, #f0fdf4)", color: "var(--theme-success-text, #15803d)" },
  'Заблокирован': { bg: "var(--theme-danger-soft, #fef2f2)", color: "var(--theme-danger-text, #dc2626)" },
};

const BaseDriversPanel = () => {
  useInterfaceLanguage();
  const user = useSelector(state => state.user);
  const canRemove = hasAccess(user, 'drivers_base_remove');
  const isMobile = useIsMobile();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDriver, setConfirmDriver] = useState(null);
  const [removing, setRemoving] = useState(false);
  const removingRef = useRef(false);
  const [removeError, setRemoveError] = useState('');
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await getBaseDrivers();
      setDrivers(res.data || []);
    } catch (e) {
      setError(tr("Не удалось загрузить водителей базы. Повторите загрузку."));
    } finally {
      setLoading(false);
    }
  }, []);

  // Load immediately on mount
  useEffect(() => { load(); }, [load]);

  const available = driver => ['', 'Свободен', 'Простаивает', 'free', 'idle'].includes(driver.status || '') && !driver.integration_active_load;
  const openRemove = driver => {
    if (!canRemove || removingRef.current || !available(driver)) return;
    setRemoveError('');
    setConfirmDriver({ ...driver });
  };
  const handleRemoveConfirm = async pin => {
    if (!canRemove || !confirmDriver || !available(confirmDriver) || removingRef.current || !/^[0-9]{4}$/.test(pin)) return;
    removingRef.current = true;
    setRemoving(true);
    setRemoveError('');
    try {
      await removeDriverFromBase(confirmDriver.id, pin);
      setNotify({ isOpen: true, message: tr("{{value0}} убран с базы", { value0: confirmDriver.employee_name }), type: 'success' });
      setConfirmDriver(null);
      await load();
    } catch (e) {
      setRemoveError(getErpErrorMessage(e, tr("Ошибка при удалении водителя")));
    } finally {
      removingRef.current = false;
      setRemoving(false);
    }
  };

  return (
    <div style={{ margin: '16px 0', background: "var(--theme-surface, #fff)", border: "1px solid var(--theme-border, #e5e7eb)", borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>

      {/* Toolbar row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: "1px solid var(--theme-border, #f3f4f6)", flexWrap: 'wrap', gap: 10 }}>
        <span style={{ fontSize: 14, color: "var(--theme-text-muted, #6b7280)", fontWeight: 500 }}>
          {loading ? tr("Загрузка...") : tr("Количество водителей на базе: {{count}}", { count: drivers.length })}
        </span>
        <button
          onClick={load}
          disabled={loading || removing}
          style={{ background: "var(--theme-surface-muted, #f9fafb)", border: "1px solid var(--theme-border, #e5e7eb)", borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: "var(--theme-text, #374151)", fontWeight: 500 }}
        >
          {loading ? '...' : tr("↻ Обновить")}
        </button>
      </div>

      {tr(error && <div role="alert" style={{ padding: 14, color: "var(--theme-danger-text, #a42c2c)", fontSize: 13 }}>{tr(error)}</div>)}
      {/* Content */}
      {loading && drivers.length === 0 ? (
        <div style={{ padding: '28px 20px', textAlign: 'center', color: "var(--theme-text-subtle, #9ca3af)", fontSize: 14 }}>{tr("Загрузка...")}</div>
      ) : drivers.length === 0 ? (
        <div style={{ padding: '28px 20px', textAlign: 'center', color: "var(--theme-text-subtle, #9ca3af)", fontSize: 14 }}>{tr("Водителей на вашей базе нет")}</div>
      ) : (
          <div>
            {isMobile ? (
              /* ── Mobile cards ── */
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 12px' }}>
                {drivers.map((d) => {
                  const sc = STATUS_COLOR[d.status] || { bg: "var(--theme-surface-muted, #f3f4f6)", color: "var(--theme-text, #374151)" };
                  const busy = !available(d) || removing;
                  return (
                    <div
                      key={d.id}
                      style={{ background: "var(--theme-surface-muted, #f9fafb)", border: "1px solid var(--theme-border, #e5e7eb)", borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}
                    >
                      {/* Top row: name + status badge */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--theme-text, #111827)", lineHeight: 1.3 }}>
                            {tr(d.employee_name || '—')}
                          </p>
                          {tr(d.employee_phone && (
                            <a href={`tel:${d.employee_phone}`} style={{ display: 'block', margin: '3px 0 0', fontSize: 13, color: "var(--theme-text-muted, #6b7280)", textDecoration: 'none' }}>
                               {tr(d.employee_phone)}
                            </a>
                          ))}
                        </div>
                        <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: sc.bg, color: sc.color, whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {tr(d.status || tr("Свободен"))}
                        </span>
                      </div>

                      {/* Bottom row: vehicle number + remove button */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Vehicle number={d.vehicle_number} model={d.vehicle_model} />
                        {canRemove && <button
                          disabled={busy}
                          onClick={() => openRemove(d)}
                          title={busy ? tr('Смена водителя, работа с базой, доступна, когда транспорт свободен или простаивает.') : tr("Убрать с базы")}
                          style={{
                            minHeight: 44, padding: '7px 16px', borderRadius: 8, border: '1.5px solid',
                            borderColor: busy ? "var(--theme-border, #e5e7eb)" : "var(--theme-danger-border, #dc2626)",
                            background: "var(--theme-surface, #fff)", color: busy ? "var(--theme-text, #d1d5db)" : "var(--theme-danger-text, #dc2626)",
                            cursor: busy ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
                          }}
                        >{tr("Убрать")}</button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ── Desktop table ── */
              <div>
                {/* Table header */}
                <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr 110px 80px', gap: '0 8px', padding: '8px 20px', background: "var(--theme-surface-muted, #f9fafb)", borderBottom: "1px solid var(--theme-border, #f3f4f6)" }}>
                  {[tr("Автомобиль"), tr("Водитель"), tr("Статус"), ''].map(h => (
                    <span key={h} style={{ fontSize: 11, fontWeight: 600, color: "var(--theme-text-subtle, #9ca3af)", textTransform: 'uppercase', letterSpacing: '0.04em' }}>{tr(h)}</span>
                  ))}
                </div>

                {drivers.map((d, idx) => {
                  const sc = STATUS_COLOR[d.status] || { bg: "var(--theme-surface-muted, #f3f4f6)", color: "var(--theme-text, #374151)" };
                  const busy = !available(d) || removing;
                  return (
                    <div
                      key={d.id}
                      style={{
                        display: 'grid', gridTemplateColumns: '170px 1fr 110px 80px',
                        gap: '0 8px', padding: '12px 20px', alignItems: 'center',
                        borderBottom: idx < drivers.length - 1 ? "1px solid var(--theme-border, #f9fafb)" : 'none',
                        background: idx % 2 === 0 ? "var(--theme-surface, #fff)" : "var(--theme-surface-muted, #fafafa)",
                      }}
                    >
                      <Vehicle number={d.vehicle_number} model={d.vehicle_model} />
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "var(--theme-text, #111827)", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {tr(d.employee_name || '—')}
                        </p>
                        {tr(d.employee_phone && (
                          <p style={{ margin: '2px 0 0', fontSize: 12, color: "var(--theme-text-muted, #6b7280)" }}>{tr(d.employee_phone)}</p>
                        ))}
                      </div>
                      <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: sc.bg, color: sc.color, whiteSpace: 'nowrap', textAlign: 'center' }}>
                        {tr(d.status || tr("Свободен"))}
                      </span>
                      {canRemove && <button
                        disabled={busy}
                        onClick={() => openRemove(d)}
                        title={busy ? tr('Смена водителя, работа с базой, доступна, когда транспорт свободен или простаивает.') : tr("Убрать с базы")}
                        style={{
                          padding: '6px 12px', borderRadius: 8, border: '1.5px solid',
                          borderColor: busy ? "var(--theme-border, #e5e7eb)" : "var(--theme-danger-border, #dc2626)",
                          background: "var(--theme-surface, #fff)", color: busy ? "var(--theme-text, #d1d5db)" : "var(--theme-danger-text, #dc2626)",
                          cursor: busy ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600,
                        }}
                      >{tr("Убрать")}</button>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )
      }

      {confirmDriver && canRemove && <DriverBaseDialog driver={confirmDriver} mode="remove" busy={removing} error={removeError}
        onClose={() => { if (!removingRef.current) setConfirmDriver(null); }}
        onConfirm={(_baseType, _baseId, pin) => handleRemoveConfirm(pin)} />}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  );
};

export default BaseDriversPanel;
